import React, { Fragment, type PropsWithChildren } from 'react';

import { formatMoney } from '@betterment/core';
import { ListRow, Separator, Tag, Text } from '@betterment/samba-react';

import { ReturnsText } from './ReturnsText';
import { SecurityPositionDetailsMode, useSecurityPositionDetailsMode } from './security-position-details-mode';

import { iconDisplayForSecurity } from '../../utils/accountSecurityHelpers';

import type { DirectSecurityPosition } from './data';
import type { SystemStyleObject } from '@betterment/samba-styled-system/types';

const RowContent: React.FC<{ position: DirectSecurityPosition }> = ({ position }) => {
  const [detailsMode] = useSecurityPositionDetailsMode();
  const { totalReturn, todaysReturn, financialSecurity } = position;

  const currentReturn = {
    [SecurityPositionDetailsMode.Total]: totalReturn,
    [SecurityPositionDetailsMode.Today]: todaysReturn,
  }[detailsMode];

  return (
    <>
      <ListRow.LeadingAsset>
        {iconDisplayForSecurity({
          symbol: position.financialSecurity.symbol,
          logo:
            financialSecurity?.__typename === 'FinancialSecurityStock' ? financialSecurity.companyProfile?.logo : null,
        })}
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent css={{ gap: 'space.50' }}>
        <Text variant="text100">{position.financialSecurity.symbol}</Text>
        {position.hasPendingOrders && <Tag accent="grey">Order pending</Tag>}
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Text variant="text100" color="text">
          {formatMoney(position.amount)}
        </Text>
        {currentReturn ? (
          <ReturnsText amount={currentReturn.amount} percent={currentReturn.percent} />
        ) : (
          <Text variant="text50" color="text.weak">
            —
          </Text>
        )}
      </ListRow.TrailingContent>
    </>
  );
};

type Props = { position: DirectSecurityPosition; css?: SystemStyleObject };
export const SecurityPositionRow: React.FC<PropsWithChildren<Props>> = props => {
  const { position, css, children } = props;
  return (
    <Fragment key={position.financialSecurity.id}>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <ListRow css={css}>
        <RowContent position={position} />
        {children}
      </ListRow>
    </Fragment>
  );
};
